/**
 * Web-specific i18n Configuration
 *
 * Handles browser language detection and localStorage persistence.
 */

import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  type LocaleConfig,
  type SupportedLocale,
} from '../types';
import { getBestLocale, i18n, isSupportedLocale } from './shared';

// ═══════════════════════════════════════════════════════════════════════════
// WEB INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════

let isInitialized = false;

/**
 * Initialize i18n for web platform
 * Handles locale detection and persistence.
 * Note: Base initialization happens synchronously in shared.ts,
 * this function just handles locale detection/switching.
 */
export async function initLocaleWeb(config: LocaleConfig = {}): Promise<void> {
  if (isInitialized) {
    return; // Already configured
  }

  const { debug = false, initialLocale, detectLanguage = false } = config;

  // Precedence: explicit initialLocale (e.g. an authenticated user's saved
  // server locale) → saved localStorage preference → browser detection (if
  // enabled) → default. The authoritative server value is applied post-auth by
  // the app calling setLocale(user.locale); this handles the pre-auth boot.
  const explicit = initialLocale && isSupportedLocale(initialLocale) ? initialLocale : null;
  const targetLocale: SupportedLocale =
    explicit ?? getSavedLocale() ?? (detectLanguage ? detectBrowserLocale() : DEFAULT_LOCALE);

  if (i18n.language !== targetLocale) {
    await i18n.changeLanguage(targetLocale);
  }

  isInitialized = true;

  if (debug && process.env['NODE_ENV'] !== 'production') {
    console.log('[i18n] Configured with locale:', i18n.language);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// LOCALE PERSISTENCE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get saved locale from localStorage
 */
export function getSavedLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null;

  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved && isSupportedLocale(saved)) {
      return saved;
    }
  } catch {
    // localStorage might be blocked
  }

  return null;
}

/**
 * Save locale to localStorage
 */
export function saveLocale(locale: SupportedLocale): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // localStorage might be blocked
  }
}

/**
 * Clear saved locale from localStorage
 */
export function clearSavedLocale(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(LOCALE_STORAGE_KEY);
  } catch {
    // localStorage might be blocked
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// BROWSER DETECTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detect browser language and return best matching locale
 */
export function detectBrowserLocale(): SupportedLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  // Try navigator.language first
  if (navigator.language) {
    return getBestLocale(navigator.language);
  }

  // Try navigator.languages array
  if (navigator.languages?.length) {
    for (const lang of navigator.languages) {
      const locale = getBestLocale(lang);
      if (locale !== DEFAULT_LOCALE) {
        return locale;
      }
    }
  }

  return DEFAULT_LOCALE;
}

// ═══════════════════════════════════════════════════════════════════════════
// CHANGE LOCALE (WEB)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Change locale and persist to localStorage
 */
export async function changeLocaleWeb(locale: SupportedLocale): Promise<void> {
  if (!isSupportedLocale(locale)) {
    console.warn(`[i18n] Unsupported locale: ${locale}`);
    return;
  }

  await i18n.changeLanguage(locale);
  saveLocale(locale);
}

export { i18n };
